import * as React from "react";
import { CircleUserRound, FileText, Package, Search, Truck } from "lucide-react";
import { useQuery } from "convex/react";
import { api } from "$convex/convexApi";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { getEnterpriseBadgeClass, getEnterpriseBadgeLabel } from "@/lib/customer-tag-colors";
import { BADGE_TONES } from "@/lib/badge-tones";
import { formatCurrency, formatDate, formatStatusLabel } from "@/lib/format";
import {
  getContractStatusBadgeClass,
  getInventoryStatusBadgeClass,
  getOrderStatusBadgeClass,
} from "@/lib/status-tag-colors";
import { cn } from "@/lib/utils";
import type { InternalSearchResponse, SearchHit } from "@/types/internal-search";

import { FlowGraph, type OrderFlowGraphData, type SelectedFlowNode } from "./FlowGraph";

type EntityType = "order" | "user" | "cargo" | "contract";
type EntitySelection = { id: string; type: EntityType; hit: SearchHit };
type SearchResultTag = { key: string; label: string; className: string };

const ENTITY_META: Record<
  EntityType,
  {
    label: string;
    pluralLabel: string;
    placeholder: string;
    searchHint: string;
    groupKey: keyof InternalSearchResponse["groups"];
    icon: React.ComponentType<{ className?: string }>;
  }
> = {
  order: {
    label: "Order",
    pluralLabel: "Orders",
    placeholder: "Search orders...",
    searchHint: "order number, customer, or company",
    groupKey: "orders",
    icon: Truck,
  },
  user: {
    label: "User",
    pluralLabel: "Users",
    placeholder: "Search users...",
    searchHint: "name, email, or company",
    groupKey: "users",
    icon: CircleUserRound,
  },
  cargo: {
    label: "Cargo",
    pluralLabel: "Cargos",
    placeholder: "Search cargos...",
    searchHint: "OCI, product, or customer details",
    groupKey: "cargos",
    icon: Package,
  },
  contract: {
    label: "Contract",
    pluralLabel: "Contracts",
    placeholder: "Search contracts...",
    searchHint: "contract number or customer",
    groupKey: "contracts",
    icon: FileText,
  },
};

const ENTITY_TYPES = Object.keys(ENTITY_META) as EntityType[];

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function HighlightedText({ text, query }: { text: string; query: string }) {
  const tokens = React.useMemo(
    () =>
      query
        .toLowerCase()
        .trim()
        .split(/\s+/)
        .filter(Boolean),
    [query],
  );

  if (!text || tokens.length === 0) return <>{text}</>;

  const pattern = tokens
    .map(escapeRegex)
    .sort((a, b) => b.length - a.length)
    .join("|");

  if (!pattern) return <>{text}</>;

  const parts = text.split(new RegExp(`(${pattern})`, "ig"));
  const lowerTokens = new Set(tokens);

  return (
    <>
      {parts.map((part, idx) => {
        const isMatch = lowerTokens.has(part.toLowerCase());
        return isMatch ? (
          <mark
            key={`${part}-${idx}`}
            className="rounded-sm bg-amber-200/70 px-0.5 text-current dark:bg-amber-500/30"
          >
            {part}
          </mark>
        ) : (
          <React.Fragment key={`${part}-${idx}`}>{part}</React.Fragment>
        );
      })}
    </>
  );
}

function getSearchResultTags(hit: SearchHit): SearchResultTag[] {
  const tags: SearchResultTag[] = [];

  if (typeof hit.tier === "number" && Number.isFinite(hit.tier) && hit.tier > 0) {
    tags.push({
      key: "tier",
      label: `Tier ${hit.tier}`,
      className: BADGE_TONES.indigo,
    });
  }

  if (typeof hit.isEnterprise === "boolean") {
    tags.push({
      key: "enterprise",
      label: getEnterpriseBadgeLabel(hit.isEnterprise),
      className: getEnterpriseBadgeClass(hit.isEnterprise),
    });
  }

  if (hit.status) {
    const statusClass =
      hit.entity === "order"
        ? getOrderStatusBadgeClass(hit.status)
        : hit.entity === "contract"
          ? getContractStatusBadgeClass(hit.status)
          : hit.entity === "cargo"
            ? getInventoryStatusBadgeClass(hit.status)
            : BADGE_TONES.slate;

    tags.push({
      key: "status",
      label: formatStatusLabel(hit.status),
      className: statusClass,
    });
  }

  return tags;
}

function formatDetailValue(fieldKey: string, value: unknown): React.ReactNode {
  if (value === null || value === undefined || value === "") {
    return <span className="text-muted-foreground">-</span>;
  }

  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }

  if (typeof value === "number") {
    const lowerKey = fieldKey.toLowerCase();
    if (/(amount|total|price|cost|subtotal|shipping)/.test(lowerKey)) {
      return formatCurrency(value);
    }
    if (/(date|at|time)$/.test(lowerKey) || lowerKey.includes("created") || lowerKey.includes("updated")) {
      const formattedDate = formatDate(value, { month: "short", day: "numeric", year: "numeric" });
      return formattedDate || String(value);
    }
    return String(value);
  }

  if (typeof value === "string") {
    const lowerKey = fieldKey.toLowerCase();
    if (/(date|at|time)$/.test(lowerKey) || lowerKey.includes("created") || lowerKey.includes("updated")) {
      const formattedDate = formatDate(value, { month: "short", day: "numeric", year: "numeric" });
      if (formattedDate) return formattedDate;
    }
    return value;
  }

  return (
    <pre className="rounded-md border bg-muted/30 p-2 text-xs whitespace-pre-wrap break-words">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

function SearchResultItem({
  entityType,
  hit,
  query,
  selected,
  onSelect,
}: {
  entityType: EntityType;
  hit: SearchHit;
  query: string;
  selected: boolean;
  onSelect: (hit: SearchHit) => void;
}) {
  const Icon = ENTITY_META[entityType].icon;
  const resultTags = getSearchResultTags(hit);
  const hasDistinctCompanyName =
    Boolean(hit.companyName) && hit.companyName?.trim().toLowerCase() !== hit.title.trim().toLowerCase();

  return (
    <CommandItem
      value={`${hit.title} ${hit.companyName ?? ""} ${hit.subtitle ?? ""}`}
      keywords={hit.tags}
      className={cn(
        "items-start gap-3 rounded-lg border border-transparent px-3 py-3 data-[selected=true]:border-border data-[selected=true]:bg-muted/70",
        selected && "border-border bg-muted/50",
      )}
      onSelect={() => onSelect(hit)}
    >
      <Icon className="mt-0.5 size-4 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium">
              <HighlightedText text={hit.title} query={query} />
            </div>
            {hasDistinctCompanyName ? (
              <div className="truncate text-xs text-muted-foreground">
                <HighlightedText text={hit.companyName ?? ""} query={query} />
              </div>
            ) : null}
            {hit.subtitle ? (
              <div className="truncate text-xs text-muted-foreground">
                <HighlightedText text={hit.subtitle} query={query} />
              </div>
            ) : null}
          </div>

          {selected ? (
            <Badge variant="outline" className="shrink-0 border-border bg-background/80 text-[11px]">
              Active
            </Badge>
          ) : null}
        </div>

        {resultTags.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {resultTags.map((tag) => (
              <Badge key={`${hit.id}-${tag.key}`} variant="outline" className={tag.className}>
                {tag.label}
              </Badge>
            ))}
          </div>
        ) : null}
      </div>
    </CommandItem>
  );
}

function SelectedEntitySummary({
  selection,
  onClear,
}: {
  selection: EntitySelection;
  onClear: () => void;
}) {
  const meta = ENTITY_META[selection.type];
  const Icon = meta.icon;
  const resultTags = getSearchResultTags(selection.hit);
  const hasDistinctCompanyName =
    Boolean(selection.hit.companyName) &&
    selection.hit.companyName?.trim().toLowerCase() !== selection.hit.title.trim().toLowerCase();

  return (
    <div className="flex flex-col gap-3 rounded-xl border bg-muted/20 p-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="gap-1 border-border bg-background/80">
            <Icon className="size-3.5" />
            {meta.label}
          </Badge>
          {resultTags.map((tag) => (
            <Badge key={`${selection.id}-${tag.key}`} variant="outline" className={tag.className}>
              {tag.label}
            </Badge>
          ))}
        </div>

        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{selection.hit.title}</p>
          {hasDistinctCompanyName ? (
            <p className="truncate text-xs text-muted-foreground">{selection.hit.companyName}</p>
          ) : null}
          {selection.hit.subtitle ? (
            <p className="truncate text-xs text-muted-foreground">{selection.hit.subtitle}</p>
          ) : null}
        </div>
      </div>

      <Button type="button" variant="ghost" size="sm" className="self-start" onClick={onClear}>
        Clear selection
      </Button>
    </div>
  );
}

function NodeDetails({ selectedNode }: { selectedNode: SelectedFlowNode }) {
  const entries = React.useMemo(
    () => Object.entries(selectedNode.data).sort(([a], [b]) => a.localeCompare(b)),
    [selectedNode.data],
  );

  return (
    <div className="space-y-3">
      <div className="rounded-md border bg-muted/20 p-3">
        <p className="text-[11px] tracking-wide text-muted-foreground uppercase">
          {formatStatusLabel(selectedNode.type)}
        </p>
        <p className="mt-1 text-sm font-semibold">{selectedNode.title}</p>
      </div>

      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">No details available for this node.</p>
      ) : (
        entries.map(([fieldKey, value]) => (
          <div key={`${selectedNode.id}-${fieldKey}`} className="rounded-md border p-2">
            <p className="text-[11px] text-muted-foreground uppercase tracking-wide">
              {formatStatusLabel(fieldKey, fieldKey)}
            </p>
            <div className="mt-1 text-sm break-words">{formatDetailValue(fieldKey, value)}</div>
          </div>
        ))
      )}
    </div>
  );
}

export default function AdminFlowPage() {
  const { authUserId, userRole } = useAuthIdentity();
  const canUseAdminQueries = Boolean(authUserId && userRole === "admin");

  const [entityType, setEntityType] = React.useState<EntityType>("order");
  const [search, setSearch] = React.useState("");
  const [selectedEntity, setSelectedEntity] = React.useState<EntitySelection | null>(null);
  const [selectedNode, setSelectedNode] = React.useState<SelectedFlowNode | null>(null);

  React.useEffect(() => {
    setSelectedEntity(null);
  }, [entityType]);

  React.useEffect(() => {
    setSelectedNode(null);
  }, [selectedEntity?.id, selectedEntity?.type]);

  const activeEntityMeta = ENTITY_META[entityType];
  const ActiveEntityIcon = activeEntityMeta.icon;
  const hasSearchQuery = search.trim().length >= 2;

  const searchResponse = useQuery(
    api.internalSearch.searchInternalGlobal,
    canUseAdminQueries && hasSearchQuery
      ? {
          authUserId,
          query: search,
          scope: entityType,
          limitPerEntity: 12,
        }
      : "skip",
  ) as InternalSearchResponse | undefined;

  const entityResults = React.useMemo<SearchHit[]>(() => {
    if (!searchResponse) return [];
    return searchResponse.groups[activeEntityMeta.groupKey];
  }, [activeEntityMeta.groupKey, searchResponse]);

  const handleEntitySelect = React.useCallback(
    (hit: SearchHit) => {
      setSelectedEntity({ id: hit.id, type: entityType, hit });
    },
    [entityType],
  );

  const clearSelection = React.useCallback(() => {
    setSelectedEntity(null);
    setSelectedNode(null);
  }, []);

  const selectedId = selectedEntity?.id ?? "";
  const shouldLoadOrderFlow =
    canUseAdminQueries && selectedEntity?.type === "order" && Boolean(selectedId);

  const orderFlow = useQuery(
    api.admin.getOrderFlowGraph,
    shouldLoadOrderFlow ? { authUserId: authUserId!, orderId: selectedId as any } : "skip",
  );

  const isSearching = canUseAdminQueries && hasSearchQuery && searchResponse === undefined;
  const showUnsupportedTypeState = Boolean(selectedEntity && selectedEntity.type !== "order");
  const showEmptyState = !selectedEntity;
  const showLoadingState = shouldLoadOrderFlow && orderFlow === undefined;
  const showNoDataState = shouldLoadOrderFlow && !orderFlow && orderFlow !== undefined;
  const graphData = shouldLoadOrderFlow && orderFlow ? (orderFlow as OrderFlowGraphData) : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Flow Explorer</h1>
        <p className="mt-1 text-muted-foreground">
          Visualize linked order entities as an interactive flow graph.
        </p>
      </div>

      {!canUseAdminQueries ? (
        <Card className="border-amber-300 bg-amber-50/60 dark:border-amber-800/60 dark:bg-amber-950/25">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Admin required</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Flow explorer requires admin access.</p>
          </CardContent>
        </Card>
      ) : null}

      <Card className="overflow-hidden">
        <CardHeader>
          <CardTitle>Entity Search</CardTitle>
          <CardDescription>
            Use the same scoped search pattern as global nav to pick an order, user, cargo, or
            contract. Graph rendering is live for orders today.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {ENTITY_TYPES.map((type) => {
              const meta = ENTITY_META[type];
              const Icon = meta.icon;

              return (
                <Button
                  key={type}
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!canUseAdminQueries}
                  className={cn(
                    "rounded-full border-border bg-background px-3.5",
                    entityType === type &&
                      "border-slate-900 bg-slate-900 text-white hover:bg-slate-800 dark:border-slate-100 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white",
                  )}
                  onClick={() => setEntityType(type)}
                >
                  <Icon className="size-3.5" />
                  {meta.label}
                </Button>
              );
            })}
          </div>

          <div className="overflow-hidden rounded-xl border bg-background">
            <Command shouldFilter={false} className="bg-transparent">
              <CommandInput
                value={search}
                onValueChange={setSearch}
                disabled={!canUseAdminQueries}
                leftSlot={
                  <Badge
                    variant="secondary"
                    className="gap-1 rounded-md border-border bg-muted/70 px-2 py-1 font-medium text-foreground"
                  >
                    <ActiveEntityIcon className="size-3.5" />
                    {activeEntityMeta.label}
                  </Badge>
                }
                placeholder={activeEntityMeta.placeholder}
              />

              <CommandList className="max-h-[320px]">
                {!canUseAdminQueries ? (
                  <CommandGroup heading="Access">
                    <CommandItem disabled className="items-start gap-3 px-3 py-3 opacity-100">
                      <Search className="mt-0.5 text-muted-foreground" />
                      <div className="space-y-1">
                        <p className="font-medium text-foreground">Admin access is required</p>
                        <p className="text-xs text-muted-foreground">
                          Flow explorer search uses protected internal records and is only available
                          to admin users.
                        </p>
                      </div>
                    </CommandItem>
                  </CommandGroup>
                ) : !hasSearchQuery ? (
                  <CommandGroup heading="Search Tips">
                    <CommandItem disabled className="items-start gap-3 px-3 py-3 opacity-100">
                      <Search className="mt-0.5 text-muted-foreground" />
                      <div className="space-y-1">
                        <p className="font-medium text-foreground">
                          Start typing to search {activeEntityMeta.pluralLabel.toLowerCase()}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Match by {activeEntityMeta.searchHint}. Use the scope pills above to
                          switch entity type.
                        </p>
                      </div>
                    </CommandItem>
                  </CommandGroup>
                ) : null}

                {isSearching ? (
                  <CommandGroup heading={activeEntityMeta.pluralLabel}>
                    <CommandItem disabled className="px-3 py-3 opacity-100">
                      <Search className="animate-pulse" />
                      <span>Searching {activeEntityMeta.pluralLabel.toLowerCase()}...</span>
                    </CommandItem>
                  </CommandGroup>
                ) : null}

                {hasSearchQuery && !isSearching && entityResults.length === 0 ? (
                  <CommandEmpty>
                    No matching {activeEntityMeta.pluralLabel.toLowerCase()} found.
                  </CommandEmpty>
                ) : null}

                {entityResults.length > 0 ? (
                  <CommandGroup heading={`${activeEntityMeta.pluralLabel} Results`}>
                    {entityResults.map((hit) => (
                      <SearchResultItem
                        key={`${hit.entity}-${hit.id}`}
                        entityType={entityType}
                        hit={hit}
                        query={searchResponse?.meta.normalizedQuery ?? search}
                        selected={selectedEntity?.id === hit.id && selectedEntity.type === entityType}
                        onSelect={handleEntitySelect}
                      />
                    ))}
                  </CommandGroup>
                ) : null}

                {searchResponse?.meta.truncated ? (
                  <CommandGroup heading="Info">
                    <CommandItem disabled className="px-3 py-3 opacity-100">
                      <Search className="text-muted-foreground" />
                      <span>Showing top matches. Keep typing to refine.</span>
                    </CommandItem>
                  </CommandGroup>
                ) : null}
              </CommandList>
            </Command>
          </div>

          {selectedEntity ? (
            <SelectedEntitySummary selection={selectedEntity} onClear={clearSelection} />
          ) : null}
        </CardContent>
      </Card>

      <section className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_340px]">
        <Card className="h-[calc(100vh-180px)] overflow-hidden">
          <CardContent className="h-full p-0">
            {showEmptyState ? (
              <div className="flex h-full flex-col items-center justify-center px-6 text-center">
                <p className="text-base font-medium">Search and pick an entity to explore its flow</p>
                <p className="mt-1 max-w-md text-sm text-muted-foreground">
                  Use the scoped command search above to select an order, user, cargo, or contract.
                </p>
              </div>
            ) : null}

            {showUnsupportedTypeState ? (
              <div className="flex h-full flex-col items-center justify-center px-6 text-center">
                <p className="text-base font-medium">Order graph view is currently available for orders</p>
                <p className="mt-1 max-w-md text-sm text-muted-foreground">
                  You selected a {ENTITY_META[selectedEntity!.type].label.toLowerCase()}. Switch
                  back to Order to render the flow graph.
                </p>
              </div>
            ) : null}

            {showLoadingState ? (
              <div className="flex h-full items-center justify-center p-8">
                <div className="w-full max-w-md space-y-3">
                  <Skeleton className="h-10 w-1/2" />
                  <Skeleton className="h-24 w-full" />
                  <Skeleton className="h-24 w-5/6" />
                  <Skeleton className="h-24 w-4/5" />
                </div>
              </div>
            ) : null}

            {showNoDataState ? (
              <div className="flex h-full flex-col items-center justify-center px-6 text-center">
                <p className="text-base font-medium">No graph data returned</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  The selected order did not return a flow graph payload from Convex.
                </p>
              </div>
            ) : null}

            {graphData ? <FlowGraph flow={graphData} onSelectNode={setSelectedNode} /> : null}
          </CardContent>
        </Card>

        <Card className="flex min-h-[360px] flex-col xl:h-[calc(100vh-180px)]">
          <CardHeader>
            <CardTitle>Node Details</CardTitle>
            <CardDescription>Click any node to inspect all available fields.</CardDescription>
          </CardHeader>
          <CardContent className="flex-1 overflow-auto">
            {selectedNode ? (
              <NodeDetails selectedNode={selectedNode} />
            ) : (
              <div className="flex h-full min-h-[220px] items-center justify-center text-center text-sm text-muted-foreground">
                Select a node in the graph to view details.
              </div>
            )}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
